

import bariLogoUrl from '@/assets/bari-logo-light.png';
import { ENV } from '@/config/environment';
import { inspectionClient } from '@/config/api';

/**
 * Comprime uma imagem via canvas: redimensiona para maxWidth e converte para JPEG com qualidade ajustável.
 */
async function compressImageBlob(blob: Blob, maxWidth = 1200, quality = 0.7): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxWidth) {
        height = Math.round(height * (maxWidth / width));
        width = maxWidth;
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas context not available')); return; }
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => reject(new Error('Failed to load image for compression'));
    img.src = URL.createObjectURL(blob);
  });
}

export async function imageUrlToBase64(url: string): Promise<string> {
try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const blob = await response.blob();

    // Comprimir imagem via canvas (redimensiona + JPEG 0.7)
    try {
      return await compressImageBlob(blob, 1200, 0.7);
    } catch {
      // Fallback: conversão direta sem compressão
      const arrayBuffer = await blob.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      const mimeType = detectImageMimeType(bytes) || blob.type || 'image/jpeg';
      if (!mimeType.startsWith('image/')) {
        throw new Error(`Não é uma imagem: tipo detectado = ${mimeType}`);
      }
      const base64 = arrayBufferToBase64(arrayBuffer);
      return `data:${mimeType};base64,${base64}`;
    }
} catch (err) {
    throw new Error(`Erro ao converter imagem para base64: ${err instanceof Error ? err.message : 'desconhecido'}`);
}
}

function detectImageMimeType(bytes: Uint8Array): string | null {
  if (bytes.length < 4) return null;

  // JPEG: FF D8 FF
  if (bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) {
    return 'image/jpeg';
  }
  // PNG: 89 50 4E 47
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) {
    return 'image/png';
  }
  // GIF: 47 49 46
  if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
    return 'image/gif';
  }
  // WebP: 52 49 46 46 ... 57 45 42 50
  if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 &&
      bytes.length > 11 && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) {
    return 'image/webp';
  }
  // BMP: 42 4D
  if (bytes[0] === 0x42 && bytes[1] === 0x4D) {
    return 'image/bmp';
  }

  return null;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export function getBariLogoUrl(): string {
return bariLogoUrl;
}

// Detecta o MIME por magic bytes em vez de confiar no blob.type, porque o S3
// pode devolver Content-Type vazio ou application/octet-stream — o browser
// tolera no <img>, mas o react-pdf rejeita data URLs sem MIME image/*.
export async function blobToBase64(blob: Blob): Promise<string> {
  const arrayBuffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);

  const detected = detectImageMimeType(bytes);
  const mimeType = detected ?? blob.type;

  if (!mimeType || !mimeType.startsWith('image/')) {
    throw new Error(
      `Blob não é uma imagem reconhecida (blob.type="${blob.type}", magic bytes não bateram com JPEG/PNG/GIF/WebP/BMP)`
    );
  }

  const base64 = arrayBufferToBase64(arrayBuffer);
  return `data:${mimeType};base64,${base64}`;
}

let bariLogoBase64Cache: string | null = null;

export async function getBariLogoBase64(): Promise<string> {
if (bariLogoBase64Cache) {
    return bariLogoBase64Cache;
}

const logoUrl = getBariLogoUrl();
bariLogoBase64Cache = await imageUrlToBase64(logoUrl);
return bariLogoBase64Cache;
}

/**
 * Verifica se a URL é do S3
 */
export function isS3Url(url: string): boolean {
  return url.includes('s3.amazonaws.com') || url.includes('amazonaws.com');
}

/**
 * Verifica se a URL presigned do S3 está expirada
 */
export function isPresignedUrlExpired(url: string): boolean {
  try {
    const urlObj = new URL(url);
    const expiresParam = urlObj.searchParams.get('X-Amz-Expires');
    const dateParam = urlObj.searchParams.get('X-Amz-Date');

    if (!expiresParam || !dateParam) {
      // Se não tem os parâmetros, assume que não está expirada
      return false;
    }

    // X-Amz-Date está no formato YYYYMMDDTHHMMSSZ
    const year = parseInt(dateParam.substring(0, 4));
    const month = parseInt(dateParam.substring(4, 6)) - 1;
    const day = parseInt(dateParam.substring(6, 8));
    const hour = parseInt(dateParam.substring(9, 11));
    const minute = parseInt(dateParam.substring(11, 13));
    const second = parseInt(dateParam.substring(13, 15));

    const signedDate = new Date(Date.UTC(year, month, day, hour, minute, second));
    const expiresInSeconds = parseInt(expiresParam);
    const expirationDate = new Date(signedDate.getTime() + expiresInSeconds * 1000);

    return new Date() > expirationDate;
  } catch {
    return false;
  }
}

/**
 * Processa URL da imagem - retorna a URL original
 */
export function processImageUrl(url: string): string {
  return url;
}

/**
 * Renova uma URL S3 expirada obtendo nova presigned URL do Docman
 */
export async function refreshS3Url(documentId: string): Promise<string> {
  const { docmanService } = await import('@/services/docman.service');
  const newUrl = await docmanService.getImageUrl(documentId);
  return newUrl;
}

/**
 * Verifica se URL está expirada e renova se necessário
 */
export async function ensureValidUrl(url: string, documentId?: string): Promise<string> {
  if (!isS3Url(url)) {
    return url;
  }

  if (!isPresignedUrlExpired(url)) {
    return url;
  }

  if (!documentId) {
    return url;
  }

  return await refreshS3Url(documentId);
}

/**
 * Processa todas as fotos do laudo e renova URLs expiradas
 */
export async function refreshLaudoPhotos(laudoData: any): Promise<any> {
  const updatedLaudo = { ...laudoData };
  let placeholderReplacements = 0;
  let undefinedReplacements = 0;

  // Processa fotos do questionário (vistoriaImovel.questionario)
  if (updatedLaudo.vistoriaImovel?.questionario) {
    for (const item of updatedLaudo.vistoriaImovel.questionario) {
      // Acessa fotos diretamente no item (formato mapeado)
      const photos = item.photos || [];
      if (photos.length > 0) {
        for (const photo of photos) {
          if (photo.fileUrl && photo.fileHash) {
            try {
              let urlParaConverter = photo.fileUrl;
              let tentativa = 1;
              let base64: string | null = null;

              // Tenta converter até 2 vezes (original + 1 renovação)
              while (tentativa <= 2 && !base64) {
                try {
                  base64 = await imageUrlToBase64(urlParaConverter);
                  photo.fileUrl = base64;
                } catch (err) {
                  if (tentativa === 1) {
                    console.warn('[refreshLaudoPhotos] Falha na 1ª tentativa (questionario photo), renovando URL', {
                      fileHash: photo.fileHash,
                      error: err,
                    });
                    try {
                      urlParaConverter = await refreshS3Url(photo.fileHash);
                      tentativa++;
                    } catch (refreshErr) {
                      console.error('[refreshLaudoPhotos] Falha ao renovar URL (questionario photo)', {
                        fileHash: photo.fileHash,
                        attempt: 1,
                        error: refreshErr,
                      });
                      break;
                    }
                  } else {
                    console.error('[refreshLaudoPhotos] Falha na 2ª tentativa (questionario photo), usando placeholder', {
                      fileHash: photo.fileHash,
                      attempt: 2,
                      error: err,
                    });
                    photo.fileUrl = getImagePlaceholder();
                    placeholderReplacements++;
                    break;
                  }
                }
              }

              if (!base64 && !photo.fileUrl.startsWith('data:image/png;base64,iVBOR')) {
                photo.fileUrl = getImagePlaceholder();
                placeholderReplacements++;
              }
            } catch (err) {
              console.error('[refreshLaudoPhotos] Erro inesperado (questionario photo)', {
                fileHash: photo.fileHash,
                error: err,
              });
              photo.fileUrl = getImagePlaceholder();
              placeholderReplacements++;
            }
          }
        }
      }

      // Processa arquivos diretamente no item (formato mapeado)
      const files = item.files || [];
      if (files.length > 0) {
        for (const file of files) {
          if (file.fileUrl && file.fileHash && isImageFile(file.fileUrl)) {
            try {
              let urlParaConverter = file.fileUrl;
              let tentativa = 1;
              let base64: string | null = null;

              // Tenta converter até 2 vezes (original + 1 renovação)
              while (tentativa <= 2 && !base64) {
                try {
                  base64 = await imageUrlToBase64(urlParaConverter);
                  file.fileUrl = base64;
                } catch (err) {
                  if (tentativa === 1) {
                    console.warn('[refreshLaudoPhotos] Falha na 1ª tentativa (questionario file), renovando URL', {
                      fileHash: file.fileHash,
                      error: err,
                    });
                    try {
                      urlParaConverter = await refreshS3Url(file.fileHash);
                      tentativa++;
                    } catch (refreshErr) {
                      console.error('[refreshLaudoPhotos] Falha ao renovar URL (questionario file)', {
                        fileHash: file.fileHash,
                        attempt: 1,
                        error: refreshErr,
                      });
                      break;
                    }
                  } else {
                    console.error('[refreshLaudoPhotos] Falha na 2ª tentativa (questionario file), usando placeholder', {
                      fileHash: file.fileHash,
                      attempt: 2,
                      error: err,
                    });
                    file.fileUrl = getImagePlaceholder();
                    placeholderReplacements++;
                    break;
                  }
                }
              }

              if (!base64 && !file.fileUrl.startsWith('data:image/png;base64,iVBOR')) {
                file.fileUrl = getImagePlaceholder();
                placeholderReplacements++;
              }
            } catch (err) {
              console.error('[refreshLaudoPhotos] Erro inesperado (questionario file)', {
                fileHash: file.fileHash,
                error: err,
              });
              file.fileUrl = getImagePlaceholder();
              placeholderReplacements++;
            }
          }
        }
      }
    }
  }

  // Processa relatorioFotografico se existir
  if (updatedLaudo.relatorioFotografico?.fotos && Array.isArray(updatedLaudo.relatorioFotografico.fotos)) {
    for (const foto of updatedLaudo.relatorioFotografico.fotos) {
      if (foto.imagemUrl && foto.documentId) {
        try {
          let urlParaConverter = foto.imagemUrl;
          let tentativa = 1;
          let base64: string | null = null;

          // Tenta converter até 2 vezes (original + 1 renovação)
          while (tentativa <= 2 && !base64) {
            try {
              base64 = await imageUrlToBase64(urlParaConverter);
              foto.imagemUrl = base64;
            } catch (err) {
              if (tentativa === 1) {
                console.warn('[refreshLaudoPhotos] Falha na 1ª tentativa (relatorioFotografico), renovando URL', {
                  documentId: foto.documentId,
                  error: err,
                });
                try {
                  urlParaConverter = await refreshS3Url(foto.documentId);
                  tentativa++;
                } catch (refreshErr) {
                  console.error('[refreshLaudoPhotos] Falha ao renovar URL (relatorioFotografico)', {
                    documentId: foto.documentId,
                    attempt: 1,
                    error: refreshErr,
                  });
                  break;
                }
              } else {
                console.error('[refreshLaudoPhotos] Falha na 2ª tentativa (relatorioFotografico), usando placeholder', {
                  documentId: foto.documentId,
                  attempt: 2,
                  error: err,
                });
                foto.imagemUrl = getImagePlaceholder();
                placeholderReplacements++;
                break;
              }
            }
          }

          if (!base64 && !foto.imagemUrl.startsWith('data:image/png;base64,iVBOR')) {
            foto.imagemUrl = getImagePlaceholder();
            placeholderReplacements++;
          }
        } catch (err) {
          console.error('[refreshLaudoPhotos] Erro inesperado (relatorioFotografico)', {
            documentId: foto.documentId,
            error: err,
          });
          foto.imagemUrl = getImagePlaceholder();
          placeholderReplacements++;
        }
      }
    }
  }

  // Processa foto de fachada se existir
  if (updatedLaudo.fotoFachadaUrl && !updatedLaudo.fotoFachadaUrl.startsWith('data:image')) {
    let urlParaConverter = updatedLaudo.fotoFachadaUrl;
    let tentativa = 1;
    let base64: string | null = null;

    while (tentativa <= 2 && !base64) {
      try {
        base64 = await imageUrlToBase64(urlParaConverter);
      } catch (err) {
        if (tentativa === 1 && updatedLaudo.fotoFachadaDocumentId) {
          console.warn('[refreshLaudoPhotos] Falha na 1ª tentativa (fotoFachada), renovando URL', {
            documentId: updatedLaudo.fotoFachadaDocumentId,
            error: err,
          });
          try {
            urlParaConverter = await refreshS3Url(updatedLaudo.fotoFachadaDocumentId);
            tentativa++;
          } catch (refreshErr) {
            console.error('[refreshLaudoPhotos] Falha ao renovar URL (fotoFachada)', {
              documentId: updatedLaudo.fotoFachadaDocumentId,
              attempt: 1,
              error: refreshErr,
            });
            break;
          }
        } else {
          console.error('[refreshLaudoPhotos] Falha definitiva (fotoFachada), removendo URL', {
            documentId: updatedLaudo.fotoFachadaDocumentId,
            attempt: tentativa,
            error: err,
          });
          break;
        }
      }
    }

    if (base64) {
      updatedLaudo.fotoFachadaUrl = base64;
      if (updatedLaudo.resumoAvaliacao) {
        updatedLaudo.resumoAvaliacao.imagemDestaqueUrl = base64;
      }
    } else {
      updatedLaudo.fotoFachadaUrl = undefined;
      if (updatedLaudo.resumoAvaliacao) {
        updatedLaudo.resumoAvaliacao.imagemDestaqueUrl = undefined;
      }
      undefinedReplacements++;
    }
  }

  // Processa fotos das amostras se existirem
  if (updatedLaudo.amostras && Array.isArray(updatedLaudo.amostras)) {
    for (const amostra of updatedLaudo.amostras) {
      // Se já é base64 JPEG/PNG válido, mantém como está
      if (amostra.imagemUrl?.startsWith('data:image/jpeg') || amostra.imagemUrl?.startsWith('data:image/png')) {
        continue;
      }

      // Se é base64 WebP, converte para JPEG via canvas (react-pdf não suporta WebP)
      if (amostra.imagemUrl?.startsWith('data:image/webp')) {
        try {
          const converted = await convertWebpBase64ToJpegInBrowser(amostra.imagemUrl);
          amostra.imagemUrl = converted;
        } catch {
          amostra.imagemUrl = undefined;
        }
        continue;
      }

      // Para amostras scraped, busca imagem via inspection service
      if (amostra.externalId) {
        try {
          const response = await inspectionClient.get(
            `/api/scraped-properties/${amostra.externalId}/image`,
            { responseType: 'arraybuffer' }
          );
          const bytes = new Uint8Array(response.data);
          const mimeType = detectImageMimeType(bytes) || response.headers['content-type'] || 'image/jpeg';
          const base64 = arrayBufferToBase64(response.data);
          let dataUrl = `data:${mimeType};base64,${base64}`;

          // react-pdf não suporta WebP — converte para JPEG
          if (mimeType === 'image/webp') {
            try {
              dataUrl = await convertWebpBase64ToJpegInBrowser(dataUrl);
            } catch {
              amostra.imagemUrl = undefined;
              continue;
            }
          }

          amostra.imagemUrl = dataUrl;
        } catch {
          amostra.imagemUrl = undefined;
        }
        continue;
      }

      // Limpa imagemUrl vazia
      if (!amostra.imagemUrl || amostra.imagemUrl.trim() === '') {
        amostra.imagemUrl = undefined;
        continue;
      }

      // Se é URL HTTP interna (S3), tenta baixar
      if (amostra.imagemUrl.startsWith('http')) {
        try {
          const base64 = await imageUrlToBase64(amostra.imagemUrl);
          amostra.imagemUrl = base64;
        } catch (err) {
          console.error('[refreshLaudoPhotos] Falha ao baixar imagem (amostra HTTP)', {
            url: amostra.imagemUrl,
            error: err,
          });
          amostra.imagemUrl = undefined;
          undefinedReplacements++;
        }
      }
    }
  }

  if (placeholderReplacements > 0 || undefinedReplacements > 0) {
    console.warn('[refreshLaudoPhotos] Fotos substituídas por placeholder/undefined', {
      placeholderReplacements,
      undefinedReplacements,
    });
  }

  return updatedLaudo;
}

export function getFileNameFromUrl(url: string): string {
try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const parts = pathname.split('/');
    const fileName = parts[parts.length - 1];

    if (!fileName) {
     return 'arquivo';
    }

    const withoutQuery = fileName.split('?')[0];
    return decodeURIComponent(withoutQuery || 'arquivo');
} catch {
    return 'arquivo';
}
}

export function isImageFile(urlOrFileName: string): boolean {
const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
const lowerUrl = urlOrFileName.toLowerCase();

return imageExtensions.some(ext => lowerUrl.includes(ext));
}

export function getFileType(urlOrFileName: string): 'image' | 'pdf' | 'document' | 'unknown' {
const lower = urlOrFileName.toLowerCase();

if (isImageFile(lower)) {
    return 'image';
}

if (lower.includes('.pdf')) {
    return 'pdf';
}

if (lower.match(/\.(doc|docx|xls|xlsx|txt|csv)$/)) {
    return 'document';
}

return 'unknown';
}

export function formatFileSize(bytes: number): string {
if (bytes === 0) return '0 Bytes';

const k = 1024;
const sizes = ['Bytes', 'KB', 'MB', 'GB'];
const i = Math.floor(Math.log(bytes) / Math.log(k));

return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}

export function getImagePlaceholder(): string {
  return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg==';
}

/**
 * Converte base64 WebP para JPEG no browser usando canvas.
 * Canvas renderiza com fundo branco, evitando artefatos amarelos de transparência.
 */
async function convertWebpBase64ToJpegInBrowser(webpBase64: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }
      // Fundo branco para eliminar transparência (evita amarelo)
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => reject(new Error('Failed to load WebP image'));
    img.src = webpBase64;
  });
}

export async function validateImageUrl(url: string): Promise<boolean> {
try {
    const response = await fetch(url, { method: 'HEAD' });
    const contentType = response.headers.get('content-type');
    return response.ok && (contentType?.startsWith('image/') ?? false);
} catch {
    return false;
}
}

/**
 * Processa e renova URLs de fotos das respostas do checklist
 * Otimização: Faz o refresh antecipado no step de Vistória
 */
export async function refreshChecklistPhotos(responses: any[]): Promise<any[]> {
  const updatedResponses = [...responses];
  let placeholderReplacements = 0;
  let photosProcessed = 0;
  let filesProcessed = 0;

  for (const response of updatedResponses) {
    // Processa fotos (answerType === 'Photo')
    if (response.answer?.photos && Array.isArray(response.answer.photos)) {
      const photos = response.answer.photos;

      for (const photo of photos) {
        if (photo.fileUrl && photo.fileHash) {
          photosProcessed++;
          try {
            let urlToConvert = photo.fileUrl;
            let attempt = 1;
            let base64: string | null = null;

            if (urlToConvert.startsWith('docman/') || !urlToConvert.startsWith('http')) {
              try {
                urlToConvert = await refreshS3Url(photo.fileHash);
              } catch (err) {
                console.error('[refreshChecklistPhotos] Falha ao renovar URL inicial da foto', {
                  fileHash: photo.fileHash,
                  attempt: 'initial',
                  error: err,
                });
                photo.fileUrl = getImagePlaceholder();
                placeholderReplacements++;
                continue;
              }
            }

            // Tenta converter até 2 vezes (original + 1 renovação)
            while (attempt <= 2 && !base64) {
              try {
                base64 = await imageUrlToBase64(urlToConvert);
                photo.fileUrl = base64;
              } catch (err) {
                if (attempt === 1) {
                  // Primeira tentativa falhou, tenta renovar a URL
                  console.warn('[refreshChecklistPhotos] Falha na 1ª tentativa, renovando URL', {
                    fileHash: photo.fileHash,
                    error: err,
                  });
                  try {
                    urlToConvert = await refreshS3Url(photo.fileHash);
                    attempt++;
                  } catch (refreshErr) {
                    console.error('[refreshChecklistPhotos] Falha ao renovar URL da foto', {
                      fileHash: photo.fileHash,
                      attempt: 1,
                      error: refreshErr,
                    });
                    break;
                  }
                } else {
                  // Segunda tentativa também falhou
                  console.error('[refreshChecklistPhotos] Falha na 2ª tentativa, usando placeholder', {
                    fileHash: photo.fileHash,
                    attempt: 2,
                    error: err,
                  });
                  photo.fileUrl = getImagePlaceholder();
                  placeholderReplacements++;
                  break;
                }
              }
            }

            if (!base64 && !photo.fileUrl.startsWith('data:image/png;base64,iVBOR')) {
              photo.fileUrl = getImagePlaceholder();
              placeholderReplacements++;
            }
          } catch (err) {
            console.error('[refreshChecklistPhotos] Erro inesperado processando foto', {
              fileHash: photo.fileHash,
              error: err,
            });
            photo.fileUrl = getImagePlaceholder();
            placeholderReplacements++;
          }
        }
      }
    }

    // Processa arquivos do tipo imagem (answerType === 'File')
    if (response.answer?.files && Array.isArray(response.answer.files)) {
      const files = response.answer.files;

      for (const file of files) {
        if (file.fileUrl && file.fileHash && isImageFile(file.fileUrl)) {
          filesProcessed++;
          try {
            let urlToConvert = file.fileUrl;
            let attempt = 1;
            let base64: string | null = null;

            while (attempt <= 2 && !base64) {
              try {
                base64 = await imageUrlToBase64(urlToConvert);
                file.fileUrl = base64;
              } catch (err) {
                if (attempt === 1) {
                  console.warn('[refreshChecklistPhotos] Falha na 1ª tentativa (file), renovando URL', {
                    fileHash: file.fileHash,
                    error: err,
                  });
                  try {
                    urlToConvert = await refreshS3Url(file.fileHash);
                    attempt++;
                  } catch (refreshErr) {
                    console.error('[refreshChecklistPhotos] Falha ao renovar URL do arquivo', {
                      fileHash: file.fileHash,
                      attempt: 1,
                      error: refreshErr,
                    });
                    break;
                  }
                } else {
                  console.error('[refreshChecklistPhotos] Falha na 2ª tentativa (file), usando placeholder', {
                    fileHash: file.fileHash,
                    attempt: 2,
                    error: err,
                  });
                  file.fileUrl = getImagePlaceholder();
                  placeholderReplacements++;
                  break;
                }
              }
            }

            if (!base64 && !file.fileUrl.startsWith('data:image/png;base64,iVBOR')) {
              file.fileUrl = getImagePlaceholder();
              placeholderReplacements++;
            }
          } catch (err) {
            console.error('[refreshChecklistPhotos] Erro inesperado processando arquivo', {
              fileHash: file.fileHash,
              error: err,
            });
            file.fileUrl = getImagePlaceholder();
            placeholderReplacements++;
          }
        }
      }
    }
  }

  if (placeholderReplacements > 0) {
    console.warn('[refreshChecklistPhotos] Fotos substituídas por placeholder', {
      placeholderReplacements,
      photosProcessed,
      filesProcessed,
      totalResponses: responses.length,
    });
  }

  return updatedResponses;
}
